import {
  useRequestAccessMutation,
  useTempAccessOptionsQuery,
} from "@/features/temp-access/api/tempAccessApi";
import { handleMutationError } from "@/shared/api/handleMutationError";
import { notify } from "@/shared/components/notify";
import { roleSelectOptions } from "@/shared/i18n/roleOptions";
import type { RequestableRole } from "@keystone/contracts/temp-access";
import { Button, Group, Modal, Select, Stack, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface RequestTempAccessModalProps {
  opened: boolean;
  onClose: () => void;
}

interface FormValues {
  orgId: string;
  role: RequestableRole | "";
  reason: string;
}

export function RequestTempAccessModal({ opened, onClose }: RequestTempAccessModalProps) {
  const { t } = useTranslation("temp-access");
  const { data: options, isLoading: optionsLoading } = useTempAccessOptionsQuery();
  const [requestAccess, { isLoading }] = useRequestAccessMutation();

  const orgOptions = useMemo(
    () => (options?.organizations ?? []).map((o) => ({ value: o.orgId, label: o.orgName })),
    [options],
  );
  const roleOptions = useMemo(
    () => roleSelectOptions(options?.requestableRoles ?? [], t),
    [options, t],
  );

  const form = useForm<FormValues>({
    initialValues: { orgId: "", role: "", reason: "" },
  });

  const firstRole = options?.requestableRoles[0];
  useEffect(() => {
    if (firstRole && !form.values.role) {
      form.setFieldValue("role", firstRole);
    }
  }, [firstRole, form]);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = form.onSubmit(async (values) => {
    let valid = true;
    if (!values.orgId) {
      form.setFieldError("orgId", t("form.errors.organizationRequired"));
      valid = false;
    }
    if (!values.role) {
      form.setFieldError("role", t("form.errors.roleRequired"));
      valid = false;
    }
    if (values.reason.trim().length < 3) {
      form.setFieldError("reason", t("form.errors.reasonRequired"));
      valid = false;
    }
    if (!valid || !values.role) return;

    try {
      await requestAccess({
        orgId: values.orgId,
        role: values.role,
        reason: values.reason.trim(),
      }).unwrap();
      notify.success("temp-access:notifications.requested");
      handleClose();
    } catch (e) {
      handleMutationError(e, "temp-access:errors.requestFailed");
    }
  });

  return (
    <Modal opened={opened} onClose={handleClose} title={t("form.title")} centered>
      <form onSubmit={handleSubmit} noValidate>
        <Stack gap="sm">
          <Select
            label={t("form.organization")}
            placeholder={t("form.organizationPlaceholder")}
            data={orgOptions}
            value={form.values.orgId || null}
            onChange={(value) => form.setFieldValue("orgId", value ?? "")}
            error={form.errors.orgId}
            required
            searchable
            disabled={optionsLoading}
            nothingFoundMessage={t("form.noOrgs")}
          />
          <Select
            label={t("form.role")}
            placeholder={t("form.rolePlaceholder")}
            data={roleOptions}
            value={form.values.role || null}
            onChange={(value) => form.setFieldValue("role", (value as RequestableRole) ?? "")}
            error={form.errors.role}
            required
            disabled={optionsLoading}
            nothingFoundMessage={t("form.noRoles")}
          />
          <Textarea
            label={t("form.reason")}
            placeholder={t("form.reasonPlaceholder")}
            value={form.values.reason}
            onChange={(e) => form.setFieldValue("reason", e.currentTarget.value)}
            error={form.errors.reason}
            required
            autosize
            minRows={2}
            maxRows={5}
            maxLength={500}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={handleClose} type="button">
              {t("common:actions.cancel")}
            </Button>
            <Button type="submit" loading={isLoading}>
              {t("form.submit")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
